import { action, computed, observable, runInAction } from 'mobx'
import ModelStore from '../../../shared-lib/stores/ModelStore'
import { CampaignMessageType, CampaignStatus, CampaignType } from '../../../graphQL/generated/enums'
import { recipientText, sentCountText, scheduledCampaignDefaults, automatedCampaignDefaults } from '../../lib/campaignHelpers'
import { createLiquidExampleData, replaceLiquidWithData } from '../../lib/liquidTextHelper'
import { getEstimatedSmsUsage } from '../../lib/crmHelper'
import { $RootStore } from '../../../src/stores/RootStore'

export const defaultSmsCampaignData = {
  _id: '',
  label: '',
  type: CampaignType.Broadcast,
  messageType: CampaignMessageType.Sms,
  messageContent: '',
  scheduledFor: undefined,
  applyToAllClients: false,
  customRecipientIds: [],
  recipientClientGroups: [],
  status: CampaignStatus.Draft,
  createdAt: new Date(),
  sentCount: 0,
}

export default class SmsCampaignModel extends ModelStore {
  @observable accessor data: SmsCampaign = { ...defaultSmsCampaignData }
  fetchMethodName = 'smsCampaign'
  saveMethodName = 'updateSmsCampaign'
  @observable accessor automatedCampaignData: AutomatedCampaign = { ...automatedCampaignDefaults }
  @observable accessor scheduledCampaignData: ScheduledCampaign = { ...scheduledCampaignDefaults }

  constructor(data: Partial<SmsCampaign>, rootStore: $RootStore) {
    super(data, rootStore)
    this.merge(data)
  }

  @computed get recipientText() {
    return recipientText(this.data, this.rootStore.i18n)
  }

  @computed get sentCountText() {
    return sentCountText(this.data, this.rootStore.i18n)
  }

  @computed get previewText() {
    const exampleDataForLiquid = createLiquidExampleData(this.rootStore.userStore.data, this.rootStore.i18n, this.rootStore.config.imagesLocation)
    return replaceLiquidWithData(this.data.messageContent, exampleDataForLiquid)
  }

  @computed get estimatedUsage() {
    return getEstimatedSmsUsage(this.previewText.length)
  }

  getScheduledCampaign = async () => {
    const { communication: { requester } } = this.rootStore
    this.setLoading(true)

    const { scheduledCampaign } = await requester.scheduledCampaign({ campaignId: this.data._id })

    this.setLoading(false)

    this.setScheduledCampaignData(scheduledCampaign
      ? scheduledCampaign
      : {
        ...scheduledCampaignDefaults,
        campaignId: this.data._id,
        messageType: CampaignMessageType.Sms,
      },
    )
  }

  getAutomatedCampaign = async () => {
    const { communication: { requester } } = this.rootStore

    const defaultData = { ...automatedCampaignDefaults, campaignId: this.data._id }

    if (this.data._id === '') {
      this.setAutomatedCampaignData(defaultData)
      return
    }

    this.setLoading(true)
    const { automatedCampaign } = await requester.automatedCampaign({ campaignId: this.data._id })
    this.setLoading(false)

    this.setAutomatedCampaignData(automatedCampaign ? automatedCampaign : defaultData)
  }

  updateScheduledCampaign = async (scheduledCampaignData: Partial<ScheduledCampaign> = {}) => {
    const scheduledCampaign = {
      ...this.scheduledCampaignData,
      ...scheduledCampaignData,
      campaignId: this.data._id,
      timeToSend: undefined,
      messageType: CampaignMessageType.Sms,
    }

    this.setLoading(true)

    const { updateScheduledCampaign } = await this.rootStore.communication.requester
      .updateScheduledCampaign({ data: scheduledCampaign })

    this.setScheduledCampaignData({
      ...this.scheduledCampaignData,
      ...updateScheduledCampaign,
    })

    this.setLoading(false)
  }

  updateAutomatedCampaign = async (automatedCampaignData: Partial<AutomatedCampaign> = {}) => {
    const automatedCampaign = {
      ...this.automatedCampaignData,
      ...automatedCampaignData,
      messageType: CampaignMessageType.Sms,
      campaignId: this.data._id,
      active: this.data.status === CampaignStatus.Active,
      sendLog: undefined,
    }

    this.setLoading(true)

    const { updateAutomatedCampaign } = await this.rootStore.communication.requester
      .updateAutomatedCampaign({ data: automatedCampaign })

    this.setAutomatedCampaignData({
      ...this.automatedCampaignData,
      ...updateAutomatedCampaign,
    })

    if (automatedCampaign.campaignId) {
      const campaign = this.rootStore.smsCampaignStore.findItemById(automatedCampaign.campaignId)
      if (campaign) {
        runInAction(() => {
          campaign.automatedCampaignData = this.automatedCampaignData
        })
      }
    }
    this.setLoading(false)
  }

  @computed get isLoyalty() {
    return this.automatedCampaignData.triggerType.includes('loyalty')
  }

  @computed get isClientReferral() {
    return this.automatedCampaignData.triggerType.includes('clientReferral')
  }

  @computed get isTransactional() {
    return this.data.triggerType?.includes('tr_')
  }

  @action setLoading = (loading: boolean) => {
    this.loading = loading
  }

  @action setAutomatedCampaignData = (automatedCampaignData: AutomatedCampaign) => {
    this.automatedCampaignData = automatedCampaignData
  }

  @action setScheduledCampaignData = (scheduledCampaignData: ScheduledCampaign) => {
    this.scheduledCampaignData = scheduledCampaignData
  }
}